import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx'

import { BaseStore } from '../__types'
import { ExtendedColumnsStore } from '../components/ExtendedColumns'
import {
  ApiStore,
  arriere,
  ArrivalsModeStore,
  FastTapeChartFiltersStore,
  NavigationStore,
  SelectRangeStore,
  TapeChartFiltersApiStore,
  TapeChartQuotesStore,
  TapeChartStateStore,
  TapesStore,
  UiStateStore,
} from '../external'

export class BaseTapeChartStore {
  constructor(
    protected getApiStore: () => Partial<ApiStore>,
    protected getFiltersApiStore: () => Partial<TapeChartFiltersApiStore>,
    protected getFilterStore: () => Partial<FastTapeChartFiltersStore>,
    protected getArrivalsModeStore: () => ArrivalsModeStore,
    protected getExtendedColumnsStore: () => Partial<ExtendedColumnsStore>,
    protected getNavigationStore: () => NavigationStore,
    protected getUiStateStore: () => UiStateStore,
    protected getStateStore: () => Partial<TapeChartStateStore>,
    protected getSelectRangeStore: () => SelectRangeStore,
    protected getTapesStore: () => TapesStore,
    protected getQuotesStore: () => TapeChartQuotesStore,
  ) {
    makeObservable(this, {
      apiStore: computed,
      tapeChartFilterApiStore: computed,
      filterStore: computed,
      arrivalModeStore: computed,
      extendedColumnsStore: computed,
      navigationStore: computed,
      uiStateStore: computed,
      stateStore: computed,
      selectRangeStore: computed,
      tapesStore: computed,
      quotesStore: computed,
    })

    this.initializeReactions()
    this.initializeDeepLinking()
  }

  get apiStore() {
    return this.getApiStore()
  }

  get tapeChartFilterApiStore() {
    return this.getFiltersApiStore()
  }

  get filterStore() {
    return this.getFilterStore()
  }

  get arrivalModeStore() {
    return this.getArrivalsModeStore()
  }

  get extendedColumnsStore() {
    return this.getExtendedColumnsStore()
  }

  get navigationStore() {
    return this.getNavigationStore()
  }

  get uiStateStore() {
    return this.getUiStateStore()
  }

  get stateStore() {
    return this.getStateStore()
  }

  get selectRangeStore() {
    return this.getSelectRangeStore()
  }

  get tapesStore() {
    return this.getTapesStore()
  }

  get quotesStore() {
    return this.getQuotesStore()
  }

  private initializeReactions() {
    reaction(
      () => this.navigationStore.searchTerms,
      () => this.navigationStore.resetPage(),
    )
    reaction(
      () => this.tapeChartFilterApiStore.unitsFilters,
      () => this.navigationStore.resetPage(),
    )
    reaction(
      () => this.navigationStore.setSortOrder,
      () => this.navigationStore.resetPage(),
    )
  }

  private initializeDeepLinking() {
    setTimeout(() => {
      this.uiStateStore.checkInitialUrlParams?.()
    }, 0)
  }
}

interface CustomStoresProps<ArriereApiType, ApiStoreType, FilterStoreType, FilterApiStoreType> {
  arriereApi?: ArriereApiType
  customApiStore?: ApiStoreType
  customFilterStore?: FilterStoreType
  customFilterApiStore?: FilterApiStoreType
}

export const createTapeChartStores = <
  ArriereApiType,
  ApiStoreType,
  FilterStoreType,
  FilterApiStoreType extends object,
>({
  arriereApi,
  customApiStore,
  customFilterStore,
  customFilterApiStore,
}: CustomStoresProps<ArriereApiType, ApiStoreType, FilterStoreType, FilterApiStoreType>) => {
  /* eslint-disable prefer-const */
  let apiStore: ApiStore
  let stateStore: TapeChartStateStore
  let filtersApiStore: TapeChartFiltersApiStore
  let filterStore: FastTapeChartFiltersStore
  let arrivalsModeStore: ArrivalsModeStore
  let extendedColumnsStore: ExtendedColumnsStore
  let navigationStore: NavigationStore
  let uiStateStore: UiStateStore
  let selectRangeStore: SelectRangeStore
  let tapesStore: TapesStore
  let quotesStore: TapeChartQuotesStore

  const getApiStore = () => customApiStore ?? apiStore
  const getStateStore = () => stateStore
  const getFiltersApiStore = () => customFilterApiStore ?? filtersApiStore
  const getFilterStore = () => customFilterStore ?? filterStore
  const getArrivalsModeStore = () => arrivalsModeStore
  const getExtendedColumnsStore = () => extendedColumnsStore
  const getNavigationStore = () => navigationStore
  const getUiStateStore = () => uiStateStore
  const getSelectRangeStore = () => selectRangeStore
  const getTapesStore = () => tapesStore
  const getQuotesStore = () => quotesStore

  filterStore = new FastTapeChartFiltersStore()
  filtersApiStore = new TapeChartFiltersApiStore(getFilterStore)
  arrivalsModeStore = new ArrivalsModeStore(getFilterStore)
  extendedColumnsStore = new ExtendedColumnsStore(getFilterStore, getArrivalsModeStore)
  navigationStore = new NavigationStore(getApiStore)
  apiStore = new ApiStore(
    arriereApi as typeof arriere,
    getStateStore,
    getNavigationStore,
    getFiltersApiStore,
  )
  stateStore = new TapeChartStateStore(getApiStore, getFilterStore)
  uiStateStore = new UiStateStore(getApiStore, getNavigationStore, getTapesStore, getFilterStore)
  selectRangeStore = new SelectRangeStore(getStateStore, getFilterStore, getQuotesStore)
  tapesStore = new TapesStore(getStateStore, getUiStateStore, getFilterStore)
  quotesStore = new TapeChartQuotesStore(getApiStore, getFilterStore, getStateStore)

  return new BaseTapeChartStore(
    getApiStore,
    getFiltersApiStore,
    getFilterStore,
    getArrivalsModeStore,
    getExtendedColumnsStore,
    getNavigationStore,
    getUiStateStore,
    getStateStore,
    getSelectRangeStore,
    getTapesStore,
    getQuotesStore,
  )
}

export const tapeChartStore = createTapeChartStores({ arriereApi: arriere })

class StoreHolder {
  private _store: BaseStore | undefined = undefined

  constructor() {
    makeObservable<StoreHolder, '_store'>(this, {
      _store: observable.ref,
      setStore: action,
    })
  }

  setStore(store: BaseStore) {
    if (this._store !== store) {
      runInAction(() => {
        this._store = store
      })
    }
  }

  get store(): BaseStore {
    if (!this._store) {
      throw new Error('No store has been set. Ensure the parent component sets the store.')
    }
    return this._store
  }
}

export const storeHolder = new StoreHolder()
